import DynamicFeedOutlinedIcon from "@mui/icons-material/DynamicFeedOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import type { GridRowHeightParams, GridRowHeightReturnValue } from "@mui/x-data-grid";
import type { DateTime } from "luxon";
import { type FC, type ReactNode, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { BetaBadge } from "../../../components/UIToolkit/Badges/BetaBadge";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { TooltipTextCell } from "../../../components/UIToolkit/Cells/TooltipTextCell/TooltipTextCell";
import type { SystemChipItem } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { FilterSidebar } from "../../../components/UIToolkit/Filters/FilterSidebar";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import type { SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { activityLogGroupedSortingKey, localStorageService } from "../../../services/LocalStorageService";
import { FONT_SIZE, INK, STATUS } from "../../../theme/tokens";
import { ADMIN_ROLES, type AdminRole, type Nurse } from "../../../utils/models";
import type { MultiSortOption, SortingState } from "../../../utils/multiColumnSorting";
import {
	ACTION_NAME_LABEL_KEY,
	ACTION_NAME_TONE,
	ACTION_NAME_VALUES,
	type ActorNames,
	actorDisplay,
	actorSearchText,
	BULK_UPDATE_ACTION_NAME,
	CHANNEL_LABEL_KEY,
	compareActors,
	composePerformedForLabel,
	composeWhatSublabel,
	composeWhatText,
	EXPAND_CONTENT_HEIGHT,
	formatActivityLogWhen,
	WHAT_SUBLABEL_ROW_HEIGHT,
} from "./activityLogGroupedDisplay";
import { GroupDetailPanel } from "./GroupDetailPanel";
import { countActiveGroupedActivityLogFilters, GroupedActivityLogFilters } from "./GroupedActivityLogFilters";
import { GroupedActivityLogRowMenu } from "./GroupedActivityLogRowMenu";
import {
	type GroupedActivityLogEntry,
	type ResolvedGroupedActivityLogEntry,
	useGroupedActivityLogQuery,
	useResolveActivityLogGroupMutation,
} from "./useGroupedActivityLogQuery";
import { useGroupedActivityLogUrlFilters } from "./useGroupedActivityLogUrlFilters";

type Props = {
	titleTrailingNode?: ReactNode;
};

/** Delays the actual fetch until filter changes settle, so rapidly toggling several filters in a row triggers one request instead of one per keystroke/click. */
const FILTER_CHANGE_DEBOUNCE_MS = 1500;

/**
 * Composes the Specific Shift filter's single date + optional start/end time into ONE absolute window
 * (`shift_window_start`/`shift_window_end` — an existing-but-unused backend param pair, reused as-is per
 * this session's plan) — deliberately never an independent time-of-day filter with no date. A missing
 * time defaults to that end of the day (start-of-day / end-of-day) so a date-only filter still composes
 * into a valid whole-day window. Rolls `end` to the next day whenever it wouldn't otherwise be strictly
 * after `start` (an explicit overnight end time <= start time) — exported for unit tests.
 */
export const composeShiftWindow = (
	date: DateTime | undefined,
	startTime: DateTime | null,
	endTime: DateTime | null,
): { start: DateTime; end: DateTime } | undefined => {
	if (!date) return undefined;
	const start = startTime ? date.set({ hour: startTime.hour, minute: startTime.minute, second: 0, millisecond: 0 }) : date.startOf("day");
	let end = endTime ? date.set({ hour: endTime.hour, minute: endTime.minute, second: 0, millisecond: 0 }) : date.endOf("day");
	if (end <= start) end = end.plus({ days: 1 });
	return { start, end };
};

export const GroupedActivityLog: FC<Props> = ({ titleTrailingNode }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const { getNurseFormattedName, isLoading: isLoadingRoster, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const { userInfo } = useAuth();
	const userId = userInfo?.id ?? "";
	// Dev Mode is support-only (this app's existing support-gating convention — see `RawPunchesChip`):
	// the SWITCH itself is hidden from non-support users (`GroupedActivityLogFilters`'s `isSupport`
	// prop), and the EFFECTIVE value consumed everywhere below is additionally clamped on this, so a
	// non-support user with a stale/tampered localStorage `true` value can never actually see a
	// Dev-Mode-gated column even without the switch being visible to them.
	const isSupport = !!userInfo?.support;

	// Dev Mode / Punches — persisted per user (localStorage), default OFF. Dev Mode gates the
	// investigative Size/Expand Count outer-grid columns (see `columns` below) and the detail grid's own
	// Transaction/Table/Changes/What columns (`GroupDetailPanel`'s `devMode` prop); Punches wires
	// `include_punches` into both grouped-log endpoints so a punch row (`nurse_in_shift_actual`) can be
	// surfaced on demand rather than always excluded.
	// Side filter panel's own open/closed state — session-only (not persisted), same as the toggle it
	// replaced when filters were toolbar-embedded.
	const [filtersCollapsed, setFiltersCollapsed] = useState(false);
	const [devMode, setDevMode] = useState(() => localStorageService.getActivityLogDevMode(userId));
	// The clamped value every Dev-Mode-gated column/prop actually reads (see `isSupport`'s doc comment
	// above) — `devMode`/`setDevMode` stay the raw localStorage-backed source of truth (still needed so
	// the switch's own `checked` value reflects what the user actually set).
	const effectiveDevMode = isSupport && devMode;
	// The stored Punches preference seeds the filter only when the URL doesn't say — a shared link
	// carries the sender's choice, and the page writes `punches` into the URL as soon as it's missing.
	// Held in state and updated alongside localStorage, so re-entering through the bare path while this
	// page stays mounted picks up the latest choice rather than the one read at mount.
	const [storedShowPunches, setStoredShowPunches] = useState(() => localStorageService.getActivityLogShowPunches(userId));

	// Every filter below lives in the URL (see `groupedActivityLogUrlState.ts`), so a filtered view is a
	// shareable link and landing on one opens it prefilled.
	const {
		actionTimeStart,
		actionTimeEnd,
		performedBy,
		performedByOptions,
		performedByOptionsLoading,
		nurseOptions,
		performedForNurse,
		performedForResolved,
		adminRoles,
		actionNames,
		channelItems,
		selectedChannels,
		shiftDate,
		shiftStartTime,
		shiftEndTime,
		shiftJobIds,
		shiftJobOptions,
		exactShiftTimes,
		showPunches,
		search,
		setActionTimeStart,
		setActionTimeEnd,
		setPerformedBy,
		setPerformedForNurse,
		setAdminRoles,
		setActionNames,
		toggleChannel,
		setShiftDate,
		setShiftStartTime,
		setShiftEndTime,
		setShiftJobIds,
		setExactShiftTimes,
		setShowPunches,
		setSearch,
		jumpToSearch,
		clearAllFilters,
	} = useGroupedActivityLogUrlFilters({ storedShowPunches });

	const shiftWindow = useMemo(() => composeShiftWindow(shiftDate, shiftStartTime, shiftEndTime), [shiftDate, shiftStartTime, shiftEndTime]);
	// Exact mode is only ever actually sent as true once the user has set BOTH times to real values — a
	// date-only pick (shiftStartTime/shiftEndTime still null, composeShiftWindow defaulting to
	// start-of-day/end-of-day) must keep behaving as "whole day" regardless of the switch, since "exact
	// midnight-to-midnight" would silently break that already-shipped, already-tested behavior.
	const shiftExactTimesApplied = exactShiftTimes && shiftStartTime !== null && shiftEndTime !== null;

	const filters = useMemo(
		() => ({
			actionTimeStart,
			actionTimeEnd,
			performedFor: performedForNurse?.value.id,
			performedBy: performedBy.length > 0 ? performedBy : undefined,
			adminRoles: adminRoles.length > 0 ? adminRoles : undefined,
			channel: selectedChannels.length > 0 ? selectedChannels.map((item) => item.value) : undefined,
			actionNames: actionNames.length > 0 ? actionNames : undefined,
			shiftWindowStart: shiftWindow?.start,
			shiftWindowEnd: shiftWindow?.end,
			shiftJobIds: shiftJobIds.length > 0 ? shiftJobIds : undefined,
			includePunches: showPunches,
			shiftExactTimes: shiftExactTimesApplied,
		}),
		[
			actionTimeStart,
			actionTimeEnd,
			performedForNurse,
			performedBy,
			adminRoles,
			selectedChannels,
			actionNames,
			shiftWindow,
			shiftJobIds,
			showPunches,
			shiftExactTimesApplied,
		],
	);
	// Debounced together with the filters, so the request unblocks with the filter it was waiting for
	// rather than one debounce window earlier, without it.
	const queryInput = useMemo(() => ({ filters, enabled: performedForResolved }), [filters, performedForResolved]);
	const debouncedQueryInput = useDebouncedValue(queryInput, FILTER_CHANGE_DEBOUNCE_MS);

	const {
		data: fetchedEntries,
		isLoading: isFetchingEntries,
		isError,
	} = useGroupedActivityLogQuery(debouncedQueryInput.filters, { enabled: debouncedQueryInput.enabled });
	// A held request isn't loading to the query, but the page is still waiting — never "no results". Rows name
	// nurses from the roster, so the grid waits for it too.
	const isLoading = isFetchingEntries || !debouncedQueryInput.enabled || isLoadingRoster;
	// Kept as its own binding (rather than a destructuring default) so the prune effect below can tell
	// "this list came back empty" apart from "this list hasn't arrived yet" — pruning against a
	// not-yet-loaded list would drop every override the moment a filter changes the query key, which is
	// exactly the bug that effect exists to fix. `?? []` is a render convenience for the grid only.
	const entries = fetchedEntries ?? [];

	// Deferred-group resolution (the grid's expand chevron — see `onRowExpandRequest` below) — resolves
	// ONE group at a time (see `useResolveActivityLogGroupMutation`'s own doc comment for why a per-row
	// local override beats writing into the list query's cache). Keyed by apiRequestId so several rows can
	// resolve concurrently without racing each other's error state. Pruned to the groups the current list
	// still contains (see the effect below).
	// Only `mutateAsync` is used, and it is destructured so the resolve handler below can be a stable
	// `useCallback` (the hook's result object is a new reference on every render).
	const { mutateAsync: resolveGroup } = useResolveActivityLogGroupMutation();
	// The scope a resolved entry is only valid within. Department is obvious; `showPunches` is the one
	// that actually bites, because it changes what the SAME api_request_id resolves to (group_size,
	// expandability, representative row) while leaving the group listed under both settings — so the
	// prune effect below, which only drops groups the new list no longer contains, cannot catch it.
	// A ref, not a closure variable: `onSuccess` fires after the request, and needs the scope as it is
	// THEN, not as it was at click time.
	const listScope = `${chosenDepartment?.department_id}|${showPunches}`;
	const listScopeRef = useRef(listScope);
	listScopeRef.current = listScope;
	// No `resolvingIds` here any more: the in-flight state IS the grid's own pending-expand set (it draws
	// the spinner in the chevron slot and ignores clicks while a row is pending), so a second copy on this
	// side would have no reader and no job — the duplicate-request guard it used to provide moved there too.
	const [resolvedOverrides, setResolvedOverrides] = useState<Record<string, ResolvedGroupedActivityLogEntry>>({});
	const [resolveErrorIds, setResolveErrorIds] = useState<ReadonlySet<string>>(new Set());
	// Groups whose uncapped "load the rest" fetch the user stopped. Lives here rather than in
	// `GroupDetailPanel` because that panel unmounts on collapse — see its `isRemainingFetchStopped` prop.
	const [stoppedRemainingFetchIds, setStoppedRemainingFetchIds] = useState<ReadonlySet<string>>(new Set());
	const handleRemainingFetchStoppedChange = useCallback((apiRequestId: string, stopped: boolean): void => {
		setStoppedRemainingFetchIds((prev) => {
			if (prev.has(apiRequestId) === stopped) return prev;
			const next = new Set(prev);
			if (stopped) next.add(apiRequestId);
			else next.delete(apiRequestId);
			return next;
		});
	}, []);
	// Prunes rather than clears, keyed off the fetch RESULT not `debouncedQueryInput`: all three maps are
	// keyed by group, so an entry stays valid while its group is still listed. Clearing on any filter
	// change meant toggling Punches (itself part of `filters`) sent an already-resolved bulk row back to its
	// unresolved placeholder. Leaves the seeded detail cache alone — that is keyed by group, not by filters.
	useEffect(() => {
		// Undefined = the list for the current filters is still in flight (a filter change is a new query
		// key, so there is no previous data to fall back on). Pruning here would clear everything.
		if (!fetchedEntries) return;
		const listedIds = new Set(fetchedEntries.map((entry) => entry.apiRequestId));
		const pruneOverrides = (prev: Record<string, ResolvedGroupedActivityLogEntry>): Record<string, ResolvedGroupedActivityLogEntry> => {
			const kept = Object.entries(prev).filter(([apiRequestId]) => listedIds.has(apiRequestId));
			return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
		};
		const pruneIds = (prev: ReadonlySet<string>): ReadonlySet<string> => {
			const kept = [...prev].filter((apiRequestId) => listedIds.has(apiRequestId));
			return kept.length === prev.size ? prev : new Set(kept);
		};
		setResolvedOverrides(pruneOverrides);
		setResolveErrorIds(pruneIds);
		setStoppedRemainingFetchIds(pruneIds);
	}, [fetchedEntries]);

	// Swaps a resolved row into place without touching sort order/position — the deferred row's own index
	// in `entries` is preserved, only its content changes, so nothing else in the grid re-sorts or flickers.
	const displayedEntries = useMemo(() => entries.map((entry) => resolvedOverrides[entry.apiRequestId] ?? entry), [entries, resolvedOverrides]);

	/**
	 * `SystemDataGrid`'s `onRowExpandRequest`: resolve the group and let the grid open the row once the
	 * resolved entry lands, via `resolvedOverrides` flipping `getRowExpandContent` to non-null.
	 *
	 * `true` while the resolve is under way for the list the user is looking at; `false` on an error or
	 * an out-of-scope completion, which makes the chevron retryable. Never rejects — the mutation has
	 * already reported the error (snackbar + `trackError`).
	 */
	const handleResolveDeferredGroup = useCallback(
		async (apiRequestId: string): Promise<boolean> => {
			const requestedScope = listScopeRef.current;
			// Event name unchanged on purpose (stable dashboards); `trigger` records the new affordance.
			track("activity_log_grouped_deferred_load_now_clicked", {
				department_id: chosenDepartment?.department_id,
				api_request_id: apiRequestId,
				trigger: "expand_chevron",
			});
			setResolveErrorIds((prev) => {
				if (!prev.has(apiRequestId)) return prev;
				const next = new Set(prev);
				next.delete(apiRequestId);
				return next;
			});
			try {
				// `includePunches: showPunches` (2026-09-09 hotfix) — mirrors the same "Punches" toggle value already
				// threaded into `GroupDetailPanel`'s sibling detail-expand query below, so a deferred group whose
				// representative row is a punch (`nurse_in_shift_actual`, tier 6) resolves instead of 404ing.
				// `.entry` only: the response is a wrapper (`ResolveActivityLogGroupResponse`) whose other half,
				// `detail`, the mutation itself has already written straight into the group-detail query cache.
				// Keeping the override map holding bare entries is what leaves `displayedEntries` below unchanged.
				const { entry: resolved } = await resolveGroup({ apiRequestId, includePunches: showPunches });
				// Out-of-scope completions report `false` so the grid stops waiting: applying one would
				// auto-open a row against a list the user has since changed (flipping Punches changes what the
				// same api_request_id resolves to).
				if (listScopeRef.current !== requestedScope) return false;
				setResolvedOverrides((prev) => ({ ...prev, [apiRequestId]: resolved }));
				return true;
			} catch {
				// Same scope test as the success path: an error line on a row the user has since filtered into a
				// different list is reporting a failure that no longer describes what they are looking at. The
				// error itself is not swallowed — `useResolveActivityLogGroupMutation`'s `onError` has already
				// shown the snackbar and tracked it.
				if (listScopeRef.current === requestedScope) {
					setResolveErrorIds((prev) => new Set(prev).add(apiRequestId));
				}
				return false;
			}
		},
		[track, chosenDepartment?.department_id, resolveGroup, showPunches],
	);

	const adminRoleItems: DropdownItem<string>[] = useMemo(() => ADMIN_ROLES.map((role) => ({ value: role, label: t(`admin.roles.${role}`) })), [t]);

	const actionNameItems: DropdownItem<string>[] = useMemo(() => ACTION_NAME_VALUES.map((value) => ({ value, label: t(ACTION_NAME_LABEL_KEY[value]) })), [t]);

	const trackFilterChange = (filter: string, extra: Record<string, unknown> = {}) => {
		track("activity_log_grouped_filter_changed", { department_id: chosenDepartment?.department_id, filter, ...extra });
	};

	const handleActionTimeStartChange = (date: DateTime) => {
		setActionTimeStart(date);
		trackFilterChange("action_time_start");
	};
	const handleActionTimeEndChange = (date: DateTime) => {
		setActionTimeEnd(date);
		trackFilterChange("action_time_end");
	};
	const handlePerformedForChange = (_event: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => {
		setPerformedForNurse(value);
		trackFilterChange("performed_for", { value: value?.value.id ?? "none" });
	};
	const handlePerformedByChange = (values: string[]) => {
		setPerformedBy(values);
		trackFilterChange("performed_by", { count: values.length });
	};
	const handleAdminRolesChange = (values: AdminRole[]) => {
		setAdminRoles(values);
		trackFilterChange("admin_roles", { count: values.length });
	};
	const handleChannelChange = (item: SystemChipItem<string>, selected: boolean) => {
		toggleChannel(item, selected);
		trackFilterChange("channel", { value: item.value, selected });
	};
	const handleActionNamesChange = (values: string[]) => {
		setActionNames(values);
		trackFilterChange("action_names", { count: values.length });
	};
	const handleShiftDateChange = (date: DateTime | undefined) => {
		setShiftDate(date);
		trackFilterChange("shift_date", { value: date ? "set" : "cleared" });
	};
	const handleShiftStartTimeChange = (value: DateTime | null) => {
		setShiftStartTime(value);
		trackFilterChange("shift_start_time");
	};
	const handleShiftEndTimeChange = (value: DateTime | null) => {
		setShiftEndTime(value);
		trackFilterChange("shift_end_time");
	};
	const handleShiftJobIdsChange = (values: string[]) => {
		setShiftJobIds(values);
		trackFilterChange("shift_jobs", { count: values.length });
	};
	const handleExactShiftTimesChange = (checked: boolean) => {
		setExactShiftTimes(checked);
		trackFilterChange("shift_exact_times", { checked });
	};
	const handleDevModeChange = (checked: boolean) => {
		setDevMode(checked);
		localStorageService.setActivityLogDevMode(checked, userId);
		trackFilterChange("dev_mode", { checked });
	};
	const handleShowPunchesChange = (checked: boolean) => {
		setShowPunches(checked);
		setStoredShowPunches(checked);
		localStorageService.setActivityLogShowPunches(checked, userId);
		trackFilterChange("show_punches", { checked });
	};
	// Resets exactly what `countActiveGroupedActivityLogFilters` counts, so the rail's badge always
	// lands on zero — including the two Display switches, which the Display section badges and which
	// therefore have to be clearable from the same action (their localStorage copies go with them, or a
	// reload would resurrect a filter the user just cleared). "Exact times" isn't counted but rides
	// along: with both times gone the switch is inert, so leaving it flipped would only be state nobody
	// can see or reach.
	//
	// The date range is deliberately NOT cleared: it is this page's primary query axis and always holds
	// a value (the same reason no badge counts it), so "cleared" would have to mean an unbounded
	// all-time scan.
	const handleClearAllFilters = () => {
		clearAllFilters();
		setStoredShowPunches(false);
		localStorageService.setActivityLogShowPunches(false, userId);
		setDevMode(false);
		localStorageService.setActivityLogDevMode(false, userId);
		trackFilterChange("clear_all");
	};
	// A detail row's "go to transaction" action (see `GroupDetailPanel`) — writes the target
	// `api_request_id` into this grid's own search box, which is already searchable on that field, so the
	// matching top-level row (if it's within the current date range/filters) surfaces without a separate
	// lookup mechanism.
	// Memoized because `getRowExpandContent` (also a `useCallback`, for the grid's pending-drain effect)
	// closes over it — an unstable identity here would defeat that one.
	const handleGoToTransaction = useCallback(
		(apiRequestId: string) => {
			jumpToSearch(apiRequestId);
			track("activity_log_group_go_to_transaction_clicked", { department_id: chosenDepartment?.department_id, api_request_id: apiRequestId });
		},
		[jumpToSearch, track, chosenDepartment?.department_id],
	);
	const formatWhen = (value: string): string => formatActivityLogWhen(value, timezone);
	const whatSublabel = (entry: GroupedActivityLogEntry): string | null => (entry.kind === "resolved" ? composeWhatSublabel(entry.details, timezone, t) : null);

	// A still-deferred group has no subject yet. See `composePerformedForLabel` for the resolved order.
	const performedForLabel = (entry: GroupedActivityLogEntry): string =>
		entry.kind === "resolved" ? composePerformedForLabel(entry, getNurseFormattedName, t) : "—";

	// The Performed By cell's render, sort and search all read the actor through this, so they can't
	// disagree about what a row shows. A still-deferred group has no actor yet.
	const entryActor = (entry: GroupedActivityLogEntry): ActorNames =>
		entry.kind === "resolved" ? { firstName: entry.performedByFirstName, username: entry.performedBy } : { firstName: null, username: null };

	const actionNameLabel = (actionName: string): string => t(ACTION_NAME_LABEL_KEY[actionName] ?? actionName);
	const channelLabel = (channel: string | null | undefined): string => (channel ? t(CHANNEL_LABEL_KEY[channel] ?? channel) : "—");

	// Thin wrapper over the extracted, unit-tested `composeWhatText` (`activityLogGroupedDisplay.ts`) — see
	// that function's own doc comment for the Call-Off client-side composition this performs. Reused by
	// both the "What" column's own render and its search index below so a user can still search by either
	// half.
	const whatText = (entry: ResolvedGroupedActivityLogEntry): string => composeWhatText(entry.what, entry.details, t);
	// Union-typed wrapper for call sites that see either row kind (search indexing, sort) — `whatText`
	// itself stays resolved-only since every OTHER caller (the "What" column's render) has already
	// narrowed via `params.row.kind === "resolved"`.
	const whatTextForAnyKind = (entry: GroupedActivityLogEntry): string => (entry.kind === "resolved" ? whatText(entry) : "");

	// `keyof (A | B)` only ever yields keys common to EVERY union member — by design, since `row[key]`
	// must be safe for whichever member `row` actually is. `actionName`/`performedBy`/etc only exist on
	// `ResolvedGroupedActivityLogEntry`, so they're legitimately outside `keyof GroupedActivityLogEntry`.
	// The sort/search runtime is still safe for a deferred row (`multiSortComparator` reads `row[field]`,
	// gets `undefined`, and `sortNullUndefinedLast`/`compareValues` already handle that) — this cast just
	// tells the type system what the runtime already guarantees, scoped to these two field lists only.
	const resolvedField = (field: keyof ResolvedGroupedActivityLogEntry): keyof GroupedActivityLogEntry => field as unknown as keyof GroupedActivityLogEntry;

	// One axis per sortable column, in column order. Every axis but `performedFor` and `performedBy`
	// sorts the raw field directly (same convention as `multiFacilityGridColumns.tsx`). Those two sort by
	// what the cell displays instead: `performedFor` is a bare nurse id (null for a swap or an approved
	// replacement, which render their nurse pair — `composePerformedForLabel`), and `performedBy` shows a
	// first name in place of the username when there is one. The sort persists per user in localStorage
	// (`activityLogGroupedSortingKey`), not in the URL — a shared link carries the view's filters, and
	// each person keeps their own sort.
	// The Dev-Mode-only columns are offered only while they're shown. Turning Dev Mode off strips a sort on
	// them (`dropUnofferedSorts`), live and saved, so a hidden column never orders the grid.
	const devModeSortingOptions: MultiSortOption<GroupedActivityLogEntry>[] = effectiveDevMode
		? [
				{ label: t("activityLog.grouped.column.size"), sortColumns: ["groupSize"] },
				{ label: t("activityLog.grouped.column.expandCount"), sortColumns: [resolvedField("expandRowCount")], sortNullUndefinedLast: true },
			]
		: [];
	const sortingOptions: MultiSortOption<GroupedActivityLogEntry>[] = [
		{ label: t("activityLog.grouped.column.when"), sortColumns: ["changedAt"] },
		{ label: t("activityLog.grouped.column.action"), sortColumns: [resolvedField("actionName")], sortNullUndefinedLast: true },
		{
			label: t("activityLog.grouped.column.performedFor"),
			sortColumns: [resolvedField("performedFor")],
			customSortFunction: (a, b): 1 | 0 | -1 => {
				const cmp = performedForLabel(a).localeCompare(performedForLabel(b));
				return cmp === 0 ? 0 : cmp > 0 ? 1 : -1;
			},
		},
		{ label: t("activityLog.grouped.column.what"), sortColumns: [resolvedField("what")], sortNullUndefinedLast: true },
		{
			label: t("activityLog.grouped.column.performedBy"),
			sortColumns: [resolvedField("performedBy")],
			// Keeps a row with no actor (and a still-deferred one) last in both directions.
			sortNullUndefinedLast: true,
			customSortFunction: (a, b) => compareActors(entryActor(a), entryActor(b)),
		},
		{ label: t("activityLog.grouped.column.channel"), sortColumns: [resolvedField("channel")], sortNullUndefinedLast: true },
		...devModeSortingOptions,
	];
	// Mirrors the backend's own default list ordering (`ORDER BY r.changed_at DESC`) so the header's
	// sort indicator reflects reality on first load instead of showing no active sort.
	const defaultSortState: SortingState<GroupedActivityLogEntry>[] = [
		{ sortColumns: ["changedAt"], sortOrder: "desc", label: t("activityLog.grouped.column.when") },
	];

	// Not memoized: closes over the (recreated-each-render) label helpers above, same rationale as
	// `ShiftLineageView`'s own column definitions.
	const columns: SystemGridColDef<GroupedActivityLogEntry>[] = [
		{
			field: "changedAt",
			headerName: t("activityLog.grouped.column.when"),
			flex: 0.7,
			minWidth: 170,
			renderCell: (params) => (
				// MUI's plain-text vertical centering trick doesn't apply once Typography sets its own
				// line-height, so every custom-rendered cell needs an explicit centered wrapper (matches
				// `teamGridColumns.tsx`'s convention) to stay aligned when a sibling row is taller.
				<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, whiteSpace: "nowrap" }}>{formatWhen(params.row.changedAt)}</Typography>
				</Box>
			),
		},
		{
			field: "actionName",
			headerName: t("activityLog.grouped.column.action"),
			flex: devMode ? 0.6 : 0.4,
			minWidth: 120,
			renderCell: (params) => {
				// A deferred group has no significance-ladder action yet (the backend hasn't resolved it) —
				// a muted, distinct "Bulk operation" badge stands in so the row reads as a real, intentional
				// state rather than a blank/broken one. See the "what" column below for the actual "Load
				// now" affordance and row size.
				if (params.row.kind === "deferred") {
					return (
						<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
							<MetaChip
								label={t("activityLog.grouped.deferred.badge")}
								icon={<DynamicFeedOutlinedIcon sx={{ fontSize: 14 }} />}
								size={"medium"}
								tone={"neutral"}
								dataTestId={`grouped-activity-log-deferred-badge-${params.row.apiRequestId}`}
							/>
						</Box>
					);
				}
				return (
					// `.MuiDataGrid-cell` centers plain text via a line-height trick that doesn't apply to a
					// block-level Box — without explicit centering this sticks to the top on a taller row (e.g.
					// alongside a "what" cell showing its shift-detail sublabel), unlike the Typography-only cells.
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", height: "100%", gap: 0.25 }}>
						<MetaChip
							label={actionNameLabel(params.row.actionName)}
							size={"medium"}
							tone={ACTION_NAME_TONE[params.row.actionName] ?? "neutral"}
							dataTestId={`grouped-activity-log-action-${params.row.apiRequestId}`}
						/>
						{params.row.actionName === BULK_UPDATE_ACTION_NAME && (
							<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, color: INK.MUTED }}>
								{t("activityLog.grouped.column.action.bulkSize", { count: params.row.groupSize })}
							</Typography>
						)}
					</Box>
				);
			},
		},
		{
			field: "performedFor",
			headerName: t("activityLog.grouped.column.performedFor"),
			flex: 1,
			minWidth: 170,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0, gap: 0.5 }}>
					{/* A nurse pair ("A → B") overflows the column's min width, so the full text lives in the tooltip. */}
					<InHouseTooltip title={performedForLabel(params.row)}>
						<Typography
							sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
						>
							{performedForLabel(params.row)}
						</Typography>
					</InHouseTooltip>
					{/* Only a single-nurse `performedFor` has a profile to link to — the swap and replacement
					pairs compose two nurse ids into one label (`composePerformedForLabel`), with no single
					profile page. A deferred row has no `performedFor` at all yet, same as the pair/nurse-count
					cases below. */}
					{params.row.kind === "resolved" && params.row.performedFor !== null && params.row.performedFor !== undefined && (
						<InHouseTooltip title={t("activityLog.grouped.performedFor.viewProfile")}>
							<IconButton
								size="small"
								aria-label={t("activityLog.grouped.performedFor.viewProfile")}
								component="a"
								href={`/${chosenDepartment.department_id}/nurse/${params.row.performedFor}`}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(event) => event.stopPropagation()}
								data-testid={`grouped-activity-log-performed-for-profile-${params.row.apiRequestId}`}
								sx={{ opacity: 0, transition: "opacity 0.1s", ".MuiDataGrid-row:hover &": { opacity: 1 }, p: 0.25, flexShrink: 0 }}
							>
								<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
							</IconButton>
						</InHouseTooltip>
					)}
				</Box>
			),
		},
		{
			field: "what",
			headerName: t("activityLog.grouped.column.what"),
			// Takes the width Performed By gave up (it shows a first name now) — the What sublabel is the
			// line that truncates first. The two flexes still sum to what they did, so no other column moves.
			flex: 1.45,
			minWidth: 170,
			renderCell: (params) => {
				if (params.row.kind === "deferred") {
					const apiRequestId = params.row.apiRequestId;
					const hasError = resolveErrorIds.has(apiRequestId);
					const label = t("activityLog.grouped.deferred.label", { count: params.row.groupSize });
					// One line, exactly like every other single-line "What" cell: the action lives in the row's
					// leading expand chevron now (`onRowExpandRequest` above), so this cell states WHAT the row
					// is and nothing else — which is also why the row no longer needs its own taller height.
					// The error REPLACES the label rather than adding a second line, keeping that shape fixed.
					const lineText = hasError ? t("activityLog.grouped.deferred.error") : label;
					return (
						<InHouseTooltip title={label}>
							<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", minWidth: 0 }}>
								<Typography
									sx={{
										fontSize: FONT_SIZE.SECONDARY,
										color: hasError ? STATUS.ERROR.dark : INK.SECONDARY,
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
									}}
								>
									{lineText}
								</Typography>
							</Box>
						</InHouseTooltip>
					);
				}
				const label = whatText(params.row);
				const sublabel = whatSublabel(params.row);
				// Tooltip on the whole cell (the sublabel composes several tier-specific parts — e.g. a
				// shift range + location + job + needed-nurses count for a Request-tier row — long enough to
				// truncate at this column's width) so the full text is never lost to ellipsis.
				return (
					<InHouseTooltip title={[label, sublabel].filter(Boolean).join(" · ")}>
						<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", gap: 0.25, minWidth: 0, overflow: "hidden" }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
								<Typography
									sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
								>
									{label}
								</Typography>
								{/* Base text is always the plain "Shift Added"/etc — a real punch (`nurse_in_shift_actual`
								Insert, per `entry.isNurseActualPunch`) additionally gets this badge rather than the
								text itself being replaced. */}
								{params.row.isNurseActualPunch && (
									<MetaChip
										label={t("activityLog.grouped.punchBadge")}
										tone="info"
										size="small"
										dataTestId={`grouped-activity-log-punch-badge-${params.row.apiRequestId}`}
										sx={{ flexShrink: 0 }}
									/>
								)}
							</Box>
							{sublabel && (
								<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, color: INK.MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
									{sublabel}
								</Typography>
							)}
						</Box>
					</InHouseTooltip>
				);
			},
		},
		{
			field: "performedBy",
			headerName: t("activityLog.grouped.column.performedBy"),
			flex: 0.8,
			minWidth: 140,
			renderCell: (params) => {
				const { label, tooltip } = actorDisplay(entryActor(params.row));
				return <TooltipTextCell text={label} tooltip={tooltip} dataTestId={`grouped-activity-log-performed-by-cell-${params.row.apiRequestId}`} />;
			},
		},
		{
			field: "channel",
			headerName: t("activityLog.grouped.column.channel"),
			// `description` is the native MUI fallback tooltip (kept for accessibility/non-sortable
			// contexts); `headerTooltip` is the rich version SortableDataGridHeader shows on this
			// sortable column. Same pair-both convention as `multiFacilityGridColumns.tsx`.
			description: t("activityLog.grouped.column.channel.tooltip"),
			headerTooltip: t("activityLog.grouped.column.channel.tooltip"),
			flex: 0.6,
			minWidth: 100,
			renderCell: (params) => (
				<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
						{params.row.kind === "resolved" ? channelLabel(params.row.channel) : "—"}
					</Typography>
				</Box>
			),
		},
	];
	// Investigative-only, same conditional-inclusion pattern as `BULK_UPDATE_ACTION_NAME`'s sublabel
	// above — mirrored in `GroupDetailPanel`'s own devMode-gated column set. Gated on the clamped
	// `effectiveDevMode`, not the raw `devMode` state (see its doc comment above). `groupSize` is common
	// to both row kinds; `expandRowCount` doesn't exist yet on a still-deferred row, hence its own
	// `renderCell` (the other columns above default to MUI's plain `row[field]` render, which would
	// otherwise print a bare blank cell for that one field on a deferred row).
	if (effectiveDevMode) {
		columns.push(
			{
				field: "groupSize",
				headerName: t("activityLog.grouped.column.size"),
				flex: 0.5,
				minWidth: 80,
			},
			{
				field: "expandRowCount",
				headerName: t("activityLog.grouped.column.expandCount"),
				flex: 0.6,
				minWidth: 120,
				renderCell: (params) => (
					<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
							{params.row.kind === "resolved" ? params.row.expandRowCount : "—"}
						</Typography>
					</Box>
				),
			},
		);
	}
	columns.push({
		field: "options",
		headerName: "",
		width: 52,
		sortable: false,
		filterable: false,
		disableColumnMenu: true,
		disableExport: true,
		excludeFromColumnPicker: true,
		renderCell: (params) => <GroupedActivityLogRowMenu apiRequestId={params.row.apiRequestId} />,
	});

	// A still-deferred row has no `isExpandable` and no detail to render yet — exactly the grid's
	// "expandable, but content has to be fetched first" case.
	// These five are memoized together because they are all in the grid's pending-drain effect deps: an
	// unstable identity for any one of them rebuilds a Map over every entry on each render mid-resolve.
	const getRowId = useCallback((row: GroupedActivityLogEntry) => row.apiRequestId, []);

	const getRowExpandContent = useCallback(
		(row: GroupedActivityLogEntry) =>
			row.kind === "resolved" && row.isExpandable ? (
				<GroupDetailPanel
					entry={row}
					getNurseFormattedName={getNurseFormattedName}
					onGoToTransaction={handleGoToTransaction}
					devMode={effectiveDevMode}
					includePunches={showPunches}
					isRemainingFetchStopped={stoppedRemainingFetchIds.has(row.apiRequestId)}
					onRemainingFetchStoppedChange={handleRemainingFetchStoppedChange}
				/>
			) : null,
		[getNurseFormattedName, handleGoToTransaction, effectiveDevMode, showPunches, stoppedRemainingFetchIds, handleRemainingFetchStoppedChange],
	);
	const getExpandContentHeight = useCallback(() => EXPAND_CONTENT_HEIGHT, []);
	// A deferred row is ALWAYS worth a chevron: whether it turns out to be expandable is one of the things
	// `/resolve` computes, and a group over `DEFERRED_GROUP_THRESHOLD` rows is expandable by construction
	// (the size arm of `is_expandable` — see the arch doc). If one does come back non-expandable, the grid
	// simply drops the spinner and the chevron with it.
	const isRowExpandable = useCallback((row: GroupedActivityLogEntry) => (row.kind === "deferred" ? true : row.isExpandable), []);
	const onRowExpandRequest = useCallback((row: GroupedActivityLogEntry) => handleResolveDeferredGroup(row.apiRequestId), [handleResolveDeferredGroup]);
	// The chevron IS the retry affordance for a group whose resolve failed, so it has to say so — the
	// generic "Load and expand row" doesn't. Only these rows get a caller string; everything else keeps
	// the grid's own wording.
	const getRowExpandTooltip = useCallback(
		(row: GroupedActivityLogEntry) => (row.kind === "deferred" && resolveErrorIds.has(row.apiRequestId) ? t("activityLog.grouped.deferred.retry") : undefined),
		[resolveErrorIds, t],
	);

	// Taller fixed height for a row that renders a two-line cell in EITHER of two independent columns:
	// the "What" cell's tier-detail sublabel (same `whatSublabel` check that decides whether the cell
	// shows it — see `WHAT_SUBLABEL_ROW_HEIGHT`'s doc comment for why), OR the "Action" cell's own
	// unconditional "{groupSize} changes" second line for a Bulk Update row (rendered regardless of
	// whether that row's own `whatSublabel` happens to be non-null — 2026-09-06 fix: previously a Bulk
	// Update row with no/short What-sublabel stayed at the default single-line height, clipping its
	// Action cell's second line). `null` for every other row defers to SystemDataGrid's own default row
	// height (`effectiveGetRowHeight` passes this straight through for non-detail rows and treats a
	// `null`/`undefined` return the same as MUI's own "use the default" convention) — never a hardcoded
	// fallback here. A deferred row has no special height any more — its "What" cell is a single line
	// like any other now that the chevron replaced the in-cell button.
	const getRowHeight = (params: GridRowHeightParams): GridRowHeightReturnValue => {
		const row = params.model as GroupedActivityLogEntry;
		if (row.kind === "deferred") return null;
		return whatSublabel(row) != null || row.actionName === BULK_UPDATE_ACTION_NAME ? WHAT_SUBLABEL_ROW_HEIGHT : null;
	};

	// The same counter the rail's own section badges use, summed — so the collapsed strip's badge can't
	// disagree with the sections it is standing in for. Not memoized, for the reason
	// `GroupedActivityLogFilters` gives about its own config: a dependency array over every filter is
	// more bug surface than a handful of length checks costs.
	const activeFilterCount = countActiveGroupedActivityLogFilters({
		performedBy,
		performedForNurse,
		adminRoles,
		selectedChannels,
		actionNames,
		shiftDate,
		shiftStartTime,
		shiftEndTime,
		shiftJobIds,
		isSupport,
		devMode,
		showPunches,
	}).total;

	const filtersComponent = (
		<GroupedActivityLogFilters
			isLoading={isLoading}
			actionTimeStart={actionTimeStart}
			actionTimeEnd={actionTimeEnd}
			onActionTimeStartChange={handleActionTimeStartChange}
			onActionTimeEndChange={handleActionTimeEndChange}
			performedByOptions={performedByOptions}
			performedByOptionsLoading={performedByOptionsLoading}
			performedBy={performedBy}
			onPerformedByChange={handlePerformedByChange}
			nurseOptions={nurseOptions}
			performedForNurse={performedForNurse}
			onPerformedForChange={handlePerformedForChange}
			adminRoles={adminRoles}
			adminRoleItems={adminRoleItems}
			onAdminRolesChange={handleAdminRolesChange}
			channelItems={channelItems}
			selectedChannels={selectedChannels}
			onChannelChange={handleChannelChange}
			actionNameItems={actionNameItems}
			actionNames={actionNames}
			onActionNamesChange={handleActionNamesChange}
			shiftDate={shiftDate}
			onShiftDateChange={handleShiftDateChange}
			shiftStartTime={shiftStartTime}
			onShiftStartTimeChange={handleShiftStartTimeChange}
			shiftEndTime={shiftEndTime}
			onShiftEndTimeChange={handleShiftEndTimeChange}
			shiftJobOptions={shiftJobOptions}
			shiftJobIds={shiftJobIds}
			onShiftJobIdsChange={handleShiftJobIdsChange}
			exactShiftTimes={exactShiftTimes}
			onExactShiftTimesChange={handleExactShiftTimesChange}
			isSupport={isSupport}
			devMode={devMode}
			onDevModeChange={handleDevModeChange}
			showPunches={showPunches}
			onShowPunchesChange={handleShowPunchesChange}
		/>
	);

	return (
		<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: 1, p: 1.5, minWidth: 0 }} data-testid="grouped-activity-log">
			<Box sx={{ display: "flex", gap: 1, alignItems: "center", pb: 1 }}>
				<Typography sx={{ color: INK.SECONDARY, fontSize: "1rem", fontWeight: "bold" }}>{t("activityLog.grouped.title")}</Typography>
				<BetaBadge label={"Beta"} />
				{titleTrailingNode}
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "row", gap: 1.5 }}>
				<FilterSidebar
					collapsed={filtersCollapsed}
					onCollapsedChange={setFiltersCollapsed}
					width={300}
					// Collapses to a full-height strip, as on the Team page, rather than to an icon pill: the
					// strip holds the column so the table doesn't jump sideways on every toggle, the width
					// animates instead of the panel vanishing in a frame, and the vertical label keeps saying
					// what the strip reopens. `collapsedIconOnly` is dropped — the rail ignores it.
					collapsedVariant="rail"
					// Same pair the Team rail passes: while the rail is a 44px strip its badge is the only thing
					// saying the table is still filtered, and "Clear all" is the way back out without reopening it.
					activeFilterCount={activeFilterCount}
					onClearAll={handleClearAllFilters}
					dataTestId="grouped-activity-log-filter-sidebar"
				>
					{filtersComponent}
				</FilterSidebar>
				<Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
					{!isLoading && !isError && !isRosterError && entries.length === 0 ? (
						// SystemDataGrid ignores its `emptyState` slot while row expansion is enabled, so the
						// "nothing found" state is rendered here instead of relying on the grid's default overlay.
						<EmptyState
							icon={<ListAltOutlinedIcon />}
							title={t("activityLog.grouped.empty.title")}
							hint={t("activityLog.grouped.empty.hint")}
							dataTestId="grouped-activity-log-empty"
						/>
					) : (
						<SystemDataGrid<GroupedActivityLogEntry>
							rows={displayedEntries}
							columns={columns}
							getRowId={getRowId}
							loading={isLoading}
							error={isError || isRosterError}
							errorMessage={t("activityLog.grouped.error")}
							sortingOptions={sortingOptions}
							defaultSortState={defaultSortState}
							localStorageKey={activityLogGroupedSortingKey}
							dropUnofferedSorts
							searchValue={search}
							onSearchChange={setSearch}
							autoExpandSingleSearchResult
							searchableColumns={[
								{ field: "apiRequestId", getValue: (row) => row.apiRequestId },
								{ field: resolvedField("actionName"), getValue: (row) => (row.kind === "resolved" ? actionNameLabel(row.actionName) : "") },
								{ field: resolvedField("performedBy"), getValue: (row) => actorSearchText(entryActor(row)) },
								{
									field: resolvedField("channel"),
									getValue: (row) => (row.kind === "resolved" && row.channel ? channelLabel(row.channel) : ""),
								},
								{ field: resolvedField("performedFor"), getValue: (row) => performedForLabel(row) },
								{ field: resolvedField("what"), getValue: (row) => whatTextForAnyKind(row) },
							]}
							searchPlaceholder={t("activityLog.grouped.searchPlaceholder")}
							getRowExpandContent={getRowExpandContent}
							getExpandContentHeight={getExpandContentHeight}
							isRowExpandable={isRowExpandable}
							onRowExpandRequest={onRowExpandRequest}
							getRowExpandTooltip={getRowExpandTooltip}
							expandedContentSx={{ py: 1.5 }}
							dataGridProps={{ getRowHeight }}
							dataTestId="grouped-activity-log-grid"
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};
